import { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import {
  Loader2, Upload, Save, Plus, Trash2, DoorOpen, Square,
  Store, PiggyBank, Move3d, Eye, Home, ChevronDown, ChevronRight, Sparkles, FileSpreadsheet, Sofa, Camera,
  RotateCw, Package,
} from "lucide-react";
import {
  FloorplanViewer, NEW_PHASES, REMODEL_PHASES, PHASE_LABEL, FLOOR_OPTIONS, ITEM_PRESETS,
} from "@/components/three/FloorplanViewer";
import SnapshotControl from "@/components/SnapshotControl";

const rid = (p) => `${p}_${Math.random().toString(36).slice(2, 10)}`;

export default function Design3DTab({ projectId }) {
  const [fp, setFp] = useState(null);
  const [phase, setPhase] = useState("finish");
  const [walk, setWalk] = useState(false);
  const [sel, setSel] = useState(null);
  const [selItem, setSelItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fromBoq, setFromBoq] = useState(false);
  const [furnished, setFurnished] = useState(false);
  const [views, setViews] = useState([]);
  const [gotoView, setGotoView] = useState(null);
  const [savingView, setSavingView] = useState(false);
  const captureRef = useRef(null);
  const fileRef = useRef(null);

  const [suppliers, setSuppliers] = useState(null);
  const [budget, setBudget] = useState(null);
  const [loadingSup, setLoadingSup] = useState(false);
  const [loadingBud, setLoadingBud] = useState(false);
  const [openSup, setOpenSup] = useState(false);
  const [openBud, setOpenBud] = useState(false);

  useEffect(() => {
    api.get(`/projects/${projectId}/floorplan`).then(({ data }) => setFp(data)).catch((e) => toast.error(apiErr(e)));
    api.get(`/projects/${projectId}/views`).then(({ data }) => setViews(data || [])).catch(() => {});
    api.get(`/projects/${projectId}/suppliers`).then(({ data }) => { if (data?.categories) setSuppliers(data); }).catch(() => {});
    api.get(`/projects/${projectId}/budget-finder`).then(({ data }) => { if (data?.labor) setBudget(data); }).catch(() => {});
  }, [projectId]);

  const phases = fp?.project_kind === "remodel" ? REMODEL_PHASES : NEW_PHASES;
  const center = useMemo(() => {
    if (!fp?.rooms?.length) return [15, 15];
    const xs = fp.rooms.map((r) => r.x + r.w / 2), zs = fp.rooms.map((r) => r.z + r.d / 2);
    return [xs.reduce((a, b) => a + b, 0) / xs.length, zs.reduce((a, b) => a + b, 0) / zs.length];
  }, [fp]);

  const update = (fn) => setFp((p) => { const n = structuredClone(p); fn(n); return n; });
  const selRoom = fp?.rooms.find((r) => r.id === sel);
  const selItemObj = (fp?.items || []).find((i) => i.id === selItem);

  const pickRoom = (id) => { setSel(id); if (id) setSelItem(null); };
  const pickItem = (id) => { setSelItem(id); if (id) setSel(null); };

  const addItem = (kind) => {
    const pr = ITEM_PRESETS[kind];
    const nr = { id: rid("item"), kind, x: Math.round(center[0]), z: Math.round(center[1]), rot: 0, w: pr.size[0], h: pr.size[1], d: pr.size[2], color: pr.color };
    update((n) => { n.items = n.items || []; n.items.push(nr); });
    setSel(null); setSelItem(nr.id);
  };
  const delItem = (id) => { update((n) => { n.items = (n.items || []).filter((i) => i.id !== id); }); if (selItem === id) setSelItem(null); };
  const setItemField = (id, f, v) => update((n) => { const i = (n.items || []).find((x) => x.id === id); if (i) i[f] = (f === "color") ? v : parseFloat(v) || 0; });
  const moveItem = (id, x, z) => update((n) => { const i = (n.items || []).find((it) => it.id === id); if (i) { i.x = x; i.z = z; } });
  const replaceItem = (id, kind) => update((n) => { const i = (n.items || []).find((x) => x.id === id); if (i) { const pr = ITEM_PRESETS[kind]; i.kind = kind; i.w = pr.size[0]; i.h = pr.size[1]; i.d = pr.size[2]; i.color = pr.color; } });

  const kindsForRoom = (room) => {
    const t = `${room.name || ""} ${room.type || ""}`.toLowerCase();
    if (/bed|primary|master/.test(t)) return [["bed", 0, -0.22], ["nightstand", -0.34, -0.3], ["nightstand", 0.34, -0.3], ["dresser", 0, 0.36]];
    if (/living|lounge|family|great/.test(t)) return [["sofa", 0, 0.33], ["coffee_table", 0, 0.05], ["tv_unit", 0, -0.38], ["armchair", -0.34, 0.08]];
    if (/kitchen/.test(t)) return [["counter", 0, -0.38], ["fridge", -0.38, -0.34], ["stove", 0.22, -0.38], ["island", 0, 0.08]];
    if (/bath|wc|powder|restroom|ensuite/.test(t)) return [["toilet", -0.3, -0.3], ["vanity", 0.3, -0.3], ["bathtub", 0, 0.34]];
    if (/dining/.test(t)) return [["dining_table", 0, 0], ["chair", -0.24, 0], ["chair", 0.24, 0], ["chair", 0, -0.24], ["chair", 0, 0.24]];
    if (/office|study|den/.test(t)) return [["desk", 0, -0.24], ["chair", 0, -0.04], ["bookshelf", 0.38, 0.22]];
    return [["sofa", 0, 0.2], ["rug", 0, -0.05]];
  };
  const autoFurnish = () => {
    const rooms = selRoom ? [selRoom] : (fp?.rooms || []);
    if (!rooms.length) { toast.error("Add a room first"); return; }
    update((n) => {
      n.items = n.items || [];
      rooms.forEach((room) => {
        const cx = room.x + room.w / 2, cz = room.z + room.d / 2;
        kindsForRoom(room).forEach(([kind, ox, oz]) => {
          const pr = ITEM_PRESETS[kind]; if (!pr) return;
          n.items.push({ id: rid("item"), kind, x: +(cx + ox * room.w).toFixed(2), z: +(cz + oz * room.d).toFixed(2), rot: 0, w: pr.size[0], h: pr.size[1], d: pr.size[2], color: pr.color });
        });
      });
    });
    setSelItem(null);
    toast.success(selRoom ? `Furnished ${selRoom.name}` : `Furnished ${rooms.length} room${rooms.length > 1 ? "s" : ""}`);
  };

  const addRoom = () => {
    const nr = { id: rid("room"), name: "New Room", type: "room", x: Math.round(center[0]), z: Math.round(center[1]), w: 12, d: 10, height: 9, wall_color: "#e5e7eb", floor: "hardwood" };
    update((n) => n.rooms.push(nr)); setSel(nr.id);
  };
  const delRoom = (id) => { update((n) => { n.rooms = n.rooms.filter((r) => r.id !== id); n.openings = n.openings.filter((o) => o.room_id !== id); }); if (sel === id) setSel(null); };
  const setRoom = (id, f, v) => update((n) => { const r = n.rooms.find((x) => x.id === id); if (r) r[f] = (f === "name" || f === "floor" || f === "wall_color" || f === "type") ? v : parseFloat(v) || 0; });
  const addOpening = (type) => { if (!sel) return toast.error("Select a room first"); update((n) => n.openings.push({ id: rid("op"), room_id: sel, wall: "s", type, offset: 0.5, width: type === "window" ? 4 : type === "arch" ? 5 : 3, height: type === "window" ? 4 : type === "arch" ? 8 : 7 })); };
  const delOpening = (id) => update((n) => { n.openings = n.openings.filter((o) => o.id !== id); });
  const setOpening = (id, f, v) => update((n) => { const o = n.openings.find((x) => x.id === id); if (o) o[f] = (f === "wall" || f === "type") ? v : parseFloat(v) || 0; });
  const addExterior = (type) => update((n) => n.exterior.push({ id: rid("ext"), type, x: Math.round(center[0]) - 5, z: Math.round(center[1]) - 12, w: 10, d: 8 }));
  const delExterior = (id) => update((n) => { n.exterior = n.exterior.filter((e) => e.id !== id); });

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put(`/projects/${projectId}/floorplan`, fp); setFp(data); toast.success("3D design saved"); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const onBlueprint = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("project_kind", fp?.project_kind || "new_build");
      const { data } = await api.post(`/projects/${projectId}/floorplan/from-blueprint`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setFp(data); setSel(null);
      toast.success(`Blueprint imported — ${data.rooms.length} rooms detected`);
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const genFromBoq = async () => {
    setFromBoq(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/floorplan/from-boq`);
      setFp(data); setSel(null);
      toast.success(`Generated from estimate — ${data.rooms.length} rooms`);
    } catch (e) { toast.error(apiErr(e)); } finally { setFromBoq(false); }
  };
  const genSuppliers = async () => { setLoadingSup(true); setOpenSup(true); try { const { data } = await api.post(`/projects/${projectId}/suppliers`); setSuppliers(data); } catch (e) { toast.error(apiErr(e)); } finally { setLoadingSup(false); } };
  const genBudget = async () => { setLoadingBud(true); setOpenBud(true); try { const { data } = await api.post(`/projects/${projectId}/budget-finder`); setBudget(data); } catch (e) { toast.error(apiErr(e)); } finally { setLoadingBud(false); } };

  const saveView = async () => {
    const snap = captureRef.current && captureRef.current();
    if (!snap?.image) return toast.error("Could not capture the view");
    const name = window.prompt("Name this view (e.g. Kitchen angle):", `View ${views.length + 1}`);
    if (name === null) return;
    setSavingView(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/views`, { name: name || `View ${views.length + 1}`, image: snap.image, phase, camera: { position: snap.position, target: snap.target } });
      setViews((v) => [...v, data]);
      toast.success("View saved — it will appear in the proposal PDF");
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingView(false); }
  };
  const delView = async (id) => { try { await api.delete(`/views/${id}`); setViews((v) => v.filter((x) => x.id !== id)); } catch (e) { toast.error(apiErr(e)); } };
  const restoreView = (v) => { setWalk(false); if (v.phase) setPhase(v.phase); if (v.camera?.position) setGotoView({ position: v.camera.position, target: v.camera.target, _ts: Date.now() }); };

  if (!fp) return <div className="grid place-items-center py-24 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="design3d-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center rounded-lg border border-border overflow-hidden">
          {["new_build", "remodel"].map((k) => (
            <button key={k} data-testid={`kind-${k}`} onClick={() => { update((n) => { n.project_kind = k; }); setPhase("finish"); }}
              className={`px-3 py-1.5 text-xs font-semibold ${fp.project_kind === k ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
              {k === "new_build" ? "New Build" : "Remodel"}
            </button>
          ))}
        </div>
        <div className="inline-flex items-center rounded-lg border border-border overflow-hidden" data-testid="phase-switch">
          {phases.map((p) => (
            <button key={p} data-testid={`phase-${p}`} onClick={() => setPhase(p)}
              className={`px-3 py-1.5 text-xs font-semibold capitalize ${phase === p ? "bg-amber-500 text-black" : "hover:bg-secondary"}`}>
              {PHASE_LABEL[p]}
            </button>
          ))}
        </div>
        <button data-testid="toggle-walk" onClick={() => setWalk((w) => !w)}
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${walk ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
          {walk ? <Move3d className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} {walk ? "Walk (WASD)" : "Orbit"}
        </button>
        <button data-testid="toggle-furnish" onClick={() => setFurnished((f) => !f)}
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${furnished ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
          <Sofa className="h-3.5 w-3.5" /> {furnished ? "Furnished" : "Furnish"}
        </button>
        <button data-testid="auto-furnish-btn" onClick={autoFurnish}
          className="inline-flex items-center gap-1.5 rounded-lg border border-primary/50 text-primary px-3 py-1.5 text-xs font-semibold hover:bg-primary/10">
          <Sparkles className="h-3.5 w-3.5" /> Auto-furnish{selRoom ? " room" : ""}
        </button>
        <button data-testid="save-view" onClick={saveView} disabled={savingView}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary disabled:opacity-60">
          {savingView ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} Save View
        </button>
        <button data-testid="from-boq" onClick={genFromBoq} disabled={fromBoq}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary disabled:opacity-60">
          {fromBoq ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />} From Estimate
        </button>
        <button data-testid="upload-blueprint" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary disabled:opacity-60">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Blueprint → 3D
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onBlueprint(e.target.files?.[0])} />
        <button data-testid="save-floorplan" onClick={save} disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold hover:opacity-90 disabled:opacity-60 ml-auto">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
        </button>
        <SnapshotControl projectId={projectId} scope="design" onRestored={async () => {
          const { data } = await api.get(`/projects/${projectId}/floorplan`); setFp(data);
        }} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* 3D viewport */}
        <div className="lg:col-span-2 rounded-xl border border-border overflow-hidden bg-[#0b1220]" style={{ height: 480 }} data-testid="design3d-canvas">
          <FloorplanViewer floorplan={fp} phase={phase} walk={walk} selected={sel} onSelect={pickRoom} furnished={furnished} captureRef={captureRef} gotoView={gotoView} selItem={selItem} onSelectItem={pickItem} onItemMove={moveItem} editItems={!walk} />
        </div>

        {/* Editor */}
        <div className="rounded-xl border border-border p-4 space-y-3 max-h-[480px] overflow-auto">
          <div className="flex items-center justify-between">
            <span className="font-bold text-sm flex items-center gap-2"><Home className="h-4 w-4 text-primary" /> Rooms</span>
            <button data-testid="add-room" onClick={addRoom} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><Plus className="h-3.5 w-3.5" /> Add</button>
          </div>
          <div className="space-y-1">
            {fp.rooms.map((r) => (
              <button key={r.id} data-testid={`room-row-${r.id}`} onClick={() => pickRoom(r.id)}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm ${sel === r.id ? "bg-primary/15 text-primary" : "hover:bg-secondary"}`}>
                <span className="truncate">{r.name}</span>
                <span className="text-xs text-muted-foreground">{r.w}×{r.d}′</span>
              </button>
            ))}
          </div>

          {selRoom && (
            <div className="rounded-lg border border-border p-3 space-y-2" data-testid="room-editor">
              <input data-testid="room-name" value={selRoom.name} onChange={(e) => setRoom(sel, "name", e.target.value)} className="w-full rounded border border-input bg-card px-2 py-1 text-sm font-semibold" />
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[["x", "X (ft)"], ["z", "Z (ft)"], ["w", "Width"], ["d", "Depth"], ["height", "Height"]].map(([f, lbl]) => (
                  <label key={f} className="flex flex-col gap-0.5"><span className="text-muted-foreground">{lbl}</span>
                    <input type="number" value={selRoom[f]} onChange={(e) => setRoom(sel, f, e.target.value)} className="rounded border border-input bg-card px-2 py-1" /></label>
                ))}
                <label className="flex flex-col gap-0.5"><span className="text-muted-foreground">Flooring</span>
                  <select data-testid="room-floor" value={selRoom.floor} onChange={(e) => setRoom(sel, "floor", e.target.value)} className="rounded border border-input bg-card px-2 py-1 capitalize">
                    {FLOOR_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select></label>
              </div>
              <label className="flex items-center gap-2 text-xs"><span className="text-muted-foreground">Wall / paint color</span>
                <input type="color" value={selRoom.wall_color} onChange={(e) => setRoom(sel, "wall_color", e.target.value)} className="h-7 w-10 rounded border border-input bg-card" /></label>

              <div className="flex flex-wrap gap-1.5 pt-1">
                <span className="text-xs text-muted-foreground w-full">Add feature:</span>
                {[["door", DoorOpen], ["arch", DoorOpen], ["window", Square]].map(([t, Icon]) => (
                  <button key={t} data-testid={`add-${t}`} onClick={() => addOpening(t)} className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:bg-secondary capitalize"><Icon className="h-3 w-3" /> {t}</button>
                ))}
              </div>
              <div className="space-y-1">
                {fp.openings.filter((o) => o.room_id === sel).map((o) => (
                  <div key={o.id} className="flex items-center gap-1 text-xs" data-testid={`opening-${o.id}`}>
                    <span className="capitalize w-12">{o.type}</span>
                    <select value={o.wall} onChange={(e) => setOpening(o.id, "wall", e.target.value)} className="rounded border border-input bg-card px-1 py-0.5">
                      {["n", "s", "e", "w"].map((w) => <option key={w} value={w}>{w.toUpperCase()}</option>)}
                    </select>
                    <input type="number" step="0.05" min="0" max="1" value={o.offset} onChange={(e) => setOpening(o.id, "offset", e.target.value)} className="w-14 rounded border border-input bg-card px-1 py-0.5" title="position 0-1" />
                    <input type="number" value={o.width} onChange={(e) => setOpening(o.id, "width", e.target.value)} className="w-12 rounded border border-input bg-card px-1 py-0.5" title="width ft" />
                    <button onClick={() => delOpening(o.id)} className="text-destructive ml-auto"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
              <button data-testid="delete-room" onClick={() => delRoom(sel)} className="inline-flex items-center gap-1 text-xs text-destructive hover:underline"><Trash2 className="h-3.5 w-3.5" /> Delete room</button>
            </div>
          )}

          <div className="pt-1 border-t border-border/60">
            <div className="flex items-center gap-2 mb-2 mt-2">
              <Package className="h-4 w-4 text-primary" />
              <span className="font-bold text-sm">Furniture & Appliances</span>
            </div>
            <p className="text-[11px] text-muted-foreground mb-2">Tap to place. Select an item then drag the on-screen arrows to move it.</p>
            <div className="space-y-2" data-testid="furniture-palette">
              {["Living", "Bedroom", "Dining", "Office", "Appliances", "Bath", "Decor"].map((cat) => {
                const kinds = Object.entries(ITEM_PRESETS).filter(([, v]) => v.cat === cat);
                return (
                  <div key={cat}>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">{cat}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {kinds.map(([k, v]) => (
                        <button key={k} data-testid={`add-item-${k}`} onClick={() => addItem(k)}
                          className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:bg-secondary hover:border-primary/60 transition-colors">
                          <Plus className="h-3 w-3" /> {v.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {selItemObj && (
              <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 space-y-2 mt-3" data-testid="item-editor">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm">{ITEM_PRESETS[selItemObj.kind]?.label || selItemObj.kind}</span>
                  <button data-testid="delete-item" onClick={() => delItem(selItemObj.id)} className="text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                <label className="flex flex-col gap-0.5 text-xs"><span className="text-muted-foreground">Replace with</span>
                  <select data-testid="replace-item" value={selItemObj.kind} onChange={(e) => replaceItem(selItemObj.id, e.target.value)} className="rounded border border-input bg-card px-2 py-1">
                    {Object.entries(ITEM_PRESETS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </label>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  {[["w", "Width"], ["d", "Depth"], ["h", "Height"]].map(([f, lbl]) => (
                    <label key={f} className="flex flex-col gap-0.5"><span className="text-muted-foreground">{lbl}</span>
                      <input type="number" step="0.5" value={selItemObj[f]} onChange={(e) => setItemField(selItemObj.id, f, e.target.value)} className="rounded border border-input bg-card px-2 py-1" /></label>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs flex-1"><RotateCw className="h-3.5 w-3.5 text-muted-foreground" />
                    <input data-testid="item-rotate" type="range" min="0" max="345" step="15" value={selItemObj.rot || 0} onChange={(e) => setItemField(selItemObj.id, "rot", e.target.value)} className="w-full" />
                    <span className="w-8 text-right text-muted-foreground">{selItemObj.rot || 0}°</span>
                  </label>
                  <label className="flex items-center gap-1.5 text-xs"><span className="text-muted-foreground">Color</span>
                    <input type="color" value={selItemObj.color} onChange={(e) => setItemField(selItemObj.id, "color", e.target.value)} className="h-7 w-9 rounded border border-input bg-card" /></label>
                </div>
              </div>
            )}
          </div>

          <div className="pt-1">
            <span className="text-xs text-muted-foreground">Outdoor features:</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {["porch", "deck", "patio"].map((t) => (
                <button key={t} data-testid={`add-${t}`} onClick={() => addExterior(t)} className="rounded border border-border px-2 py-1 text-xs hover:bg-secondary capitalize">+ {t}</button>
              ))}
            </div>
            {fp.exterior.map((e) => (
              <div key={e.id} className="flex items-center justify-between text-xs mt-1 px-1">
                <span className="capitalize">{e.type} · {e.w}×{e.d}′</span>
                <button onClick={() => delExterior(e.id)} className="text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Saved views */}
      {views.length > 0 && (
        <div className="rounded-xl border border-border p-4" data-testid="saved-views">
          <div className="text-sm font-bold mb-2 flex items-center gap-2"><Camera className="h-4 w-4 text-primary" /> Saved 3D views <span className="font-normal text-muted-foreground text-xs">(included in the proposal PDF)</span></div>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {views.map((v) => (
              <div key={v.id} className="relative shrink-0 w-44" data-testid={`view-${v.id}`}>
                <button onClick={() => restoreView(v)} className="block w-full rounded-lg overflow-hidden border border-border hover:border-primary transition-colors">
                  <img src={`data:image/jpeg;base64,${v.image_b64}`} alt={v.name} className="w-full h-28 object-cover" />
                  <div className="px-2 py-1 text-xs font-medium truncate text-left">{v.name}</div>
                </button>
                <button onClick={() => delView(v.id)} data-testid={`del-view-${v.id}`} className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-md bg-black/60 text-white hover:bg-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suppliers */}
      <div className="rounded-xl border border-border">
        <button data-testid="suppliers-header" onClick={() => setOpenSup((o) => !o)} className="flex w-full items-center justify-between px-4 py-3">
          <span className="font-bold text-sm flex items-center gap-2"><Store className="h-4 w-4 text-primary" /> Where to buy — suppliers & wholesalers</span>
          {openSup ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {openSup && (
          <div className="px-4 pb-4 space-y-3">
            <button data-testid="gen-suppliers" onClick={genSuppliers} disabled={loadingSup} className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold disabled:opacity-60">
              {loadingSup ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {suppliers ? "Refresh suggestions" : "Get supplier suggestions"}
            </button>
            {suppliers?.categories?.map((c, i) => (
              <div key={i} className="rounded-lg border border-border p-3">
                <div className="font-semibold text-sm mb-1">{c.category}</div>
                {c.items?.map((it, j) => (
                  <div key={j} className="text-xs text-muted-foreground mb-1.5">
                    <span className="font-medium text-foreground">{it.name}</span> — {it.suppliers?.join(", ")}
                    <span className="text-emerald-500"> · {it.price_range}</span>
                    {it.tip && <div className="italic opacity-80">{it.tip}</div>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Budget finder */}
      <div className="rounded-xl border border-border">
        <button data-testid="budget-header" onClick={() => setOpenBud((o) => !o)} className="flex w-full items-center justify-between px-4 py-3">
          <span className="font-bold text-sm flex items-center gap-2"><PiggyBank className="h-4 w-4 text-emerald-500" /> Budget-friendly best rates</span>
          {openBud ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {openBud && (
          <div className="px-4 pb-4 space-y-3">
            <button data-testid="gen-budget" onClick={genBudget} disabled={loadingBud} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-60">
              {loadingBud ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {budget ? "Refresh" : "Find best rates"}
            </button>
            {budget && (
              <>
                {budget.summary && <p className="text-sm text-muted-foreground">{budget.summary} {budget.total_potential_savings_pct > 0 && <span className="text-emerald-500 font-semibold">(~{budget.total_potential_savings_pct}% potential savings)</span>}</p>}
                {[["labor", "Labor"], ["materials", "Materials"], ["equipment", "Equipment"]].map(([k, lbl]) => (
                  (budget[k]?.length > 0) && (
                    <div key={k} className="rounded-lg border border-border p-3">
                      <div className="font-semibold text-sm mb-1.5">{lbl}</div>
                      {budget[k].map((it, i) => (
                        <div key={i} className="text-xs mb-1.5">
                          <span className="font-medium">{it.item}</span>
                          <span className="text-muted-foreground"> · market {it.market_rate} → </span>
                          <span className="text-emerald-500 font-semibold">{it.budget_rate}</span>
                          {it.how && <div className="text-muted-foreground italic opacity-80">{it.how}</div>}
                        </div>
                      ))}
                    </div>
                  )
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
